/**
 * Event color palette for the calendar page: ten curated hex colors drawn from the app's
 * avatar/chart ramp (emerald/teal/lime/amber/orange/rose/pink/fuchsia/violet plus a neutral),
 * so a default-colored event reads as part of the Syncra dark theme. The server accepts only
 * `#rrggbb` literals (docs/contract.md, migration 1005): validation here mirrors that shape so
 * the palette never sends a value the service would reject with 400 invalid_request.
 */

/** The ten palette swatches, lowest key = the default. */
export const EVENT_COLORS: readonly string[] = [
  '#10B981',
  '#14B8A6',
  '#84CC16',
  '#F59E0B',
  '#F97316',
  '#F43F5E',
  '#EC4899',
  '#D946EF',
  '#8B5CF6',
  '#71717A',
];

/** The color a palette-less event renders with in the grid (the app's emerald accent). */
export const DEFAULT_EVENT_COLOR = EVENT_COLORS[0] ?? '#10B981';

const HEX_PATTERN = /^#([0-9a-f]{6})$/i;

/** True when `raw` is exactly an #rrggbb literal (the only color shape the server accepts). */
export function isValidHexColor(raw: string): boolean {
  return HEX_PATTERN.test(raw.trim());
}

/**
 * Canonical #rrggbb form of `raw` (trimmed, lowercased): accepts the `#rrggbb` literal and the
 * bare `rrggbb` form (the leading `#` is added). Null for anything else - the caller renders
 * the field error instead of sending a value the server would reject.
 */
export function normalizeHexColor(raw: string): string | null {
  const candidate = raw.trim();
  const withHash = candidate.startsWith('#') ? candidate : `#${candidate}`;
  const match = HEX_PATTERN.exec(withHash);
  return match === null ? null : `#${withHash.slice(1).toLowerCase()}`;
}

/**
 * Readable text color over `hex`: near-black text on light fills, near-white on dark ones,
 * from the relative-luminance rule (WCAG). Event chips render the title over the color fill,
 * so a wrong pair is unreadable, not just ugly.
 */
export function textColorFor(hex: string): string {
  const normalized = normalizeHexColor(hex);
  if (normalized === null) {
    return '#fafafa';
  }
  const channels = [1, 3, 5].map((offset) => parseInt(normalized.slice(offset, offset + 2), 16));
  const [r = 0, g = 0, b = 0] = channels;
  const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  return luminance > 0.55 ? '#0b0f0e' : '#fafafa';
}
